/**
 * RequireAuth Component
 * Route guard that checks if user is authenticated
 * Redirects to login if not, preserving the intended destination
 */

import { useEffect } from 'react'
import { Navigate, useLocation, Outlet } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/use-auth'
import { LoadingSpinner } from '@/components/common/loading-spinner'

interface RequireAuthProps {
  children?: React.ReactNode
  fallback?: React.ReactNode
}

export function RequireAuth({ children, fallback }: RequireAuthProps) {
  const { isAuthenticated, isInitialized } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking auth state
  if (!isInitialized) {
    if (fallback) {
      return <>{fallback}</>
    }

    return (
      <div className={cn(
        'min-h-screen bg-[#ffffff]',
        'flex items-center justify-center'
      )}>
        <LoadingSpinner size="lg" text="Checking authentication..." />
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Encode the current location to redirect back after login
    const returnUrl = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />
  }

  // Render children or outlet for nested routes
  return children ? <>{children}</> : <Outlet />
}

export default RequireAuth
